import React, { useState, Component } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { profileCountry, profileCity, profileStreet, makeCompanyName } from '../../../shared/config/localeProfile';
import { ArrowLeftIcon, BuildingIcon, UserIcon, SearchIcon, Loader2Icon, CheckCircle2Icon, CreditCardIcon, LandmarkIcon, SmartphoneIcon, PenLineIcon, LinkIcon, CopyIcon, CheckIcon } from 'lucide-react';
import { SearchableSelect } from '../../../shared/ui/SearchableSelect';
import { MALAYSIAN_STATES, MALAYSIAN_BANKS, mockSuppliers } from '../mocks/mockSuppliers';
export function SupplierForm() {
  const {
    t
  } = useTranslation('suppliers');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const supplierId = searchParams.get('id');
  const isEditing = !!supplierId;
  const editingSupplier = isEditing ? mockSuppliers.find(s => s.id === supplierId) || null : null;
  const [entryMethod, setEntryMethod] = useState<'manual' | 'invite'>('manual');
  const [supplierType, setSupplierType] = useState<'company' | 'individual'>(editingSupplier?.supplierType || 'company');
  // DBD Lookup State
  const [isLookingUp, setIsLookingUp] = useState(false);
  const [lookupSuccess, setLookupSuccess] = useState(false);
  const [lookupError, setLookupError] = useState('');
  const [formData, setFormData] = useState({
    // Shared
    email: editingSupplier?.email || '',
    phone: editingSupplier?.phone || '',
    useAsBillingEmail: editingSupplier?.useAsBillingEmail ?? true,
    addressLine1: editingSupplier?.addressLine1 || '',
    addressLine2: editingSupplier?.addressLine2 || '',
    city: editingSupplier?.city || '',
    province: editingSupplier?.province || '',
    country: profileCountry(),
    postcode: editingSupplier?.postcode || '',
    internalReference: editingSupplier?.internalReference || '',
    // Company
    taxId: editingSupplier?.taxId || '',
    companyNameTh: editingSupplier?.companyNameTh || '',
    companyNameEn: editingSupplier?.companyNameEn || '',
    branch: editingSupplier?.branch || '00000',
    vatRegistered: editingSupplier?.vatRegistered ?? false,
    contactPerson: editingSupplier?.contactPerson || '',
    // Individual
    idType: editingSupplier?.idType || 'citizen_id',
    idNumber: editingSupplier?.idNumber || '',
    fullNameTh: editingSupplier?.fullNameTh || '',
    fullNameEn: editingSupplier?.fullNameEn || '',
    individualTaxId: editingSupplier?.individualTaxId || '',
    // Pay-in method
    payInMethod: editingSupplier?.payInMethod || 'acquiring',
    cardholderName: editingSupplier?.cardholderName || '',
    cardNumber: editingSupplier?.cardNumber || '',
    expiryDate: editingSupplier?.expiryDate || '',
    // Payout method
    payoutMethod: editingSupplier?.payoutMethod || 'bank_account',
    bankName: editingSupplier?.bankName || '',
    accountNumber: editingSupplier?.accountNumber || '',
    accountName: editingSupplier?.accountName || '',
    promptPayType: editingSupplier?.promptPayType || 'phone',
    promptPayId: editingSupplier?.promptPayId || '',
    promptPayName: editingSupplier?.promptPayName || ''
  });
  // DBD lookup state for API response metadata
  const [dbdMeta, setDbdMeta] = useState<{
    registerDate?: string;
    juristicStatus?: string;
    juristicType?: string;
    isMockFallback?: boolean;
  }>({});
  const [copied, setCopied] = useState(false);
  const [inviteName, setInviteName] = useState('');
  const [linkGenerated, setLinkGenerated] = useState(false);
  const inviteLink = `${window.location.origin}/supplier-onboarding/abc123def`;
  const handleCopyLink = () => {
    navigator.clipboard.writeText(inviteLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  const handleLookup = async () => {
    if (!formData.taxId || formData.taxId.length !== 13) {
      setLookupError('Please enter a valid 13-digit Tax ID / Registration Number');
      setLookupSuccess(false);
      return;
    }
    setLookupError('');
    setIsLookingUp(true);
    setLookupSuccess(false);
    setDbdMeta({});
    const DBD_API_BASE = 'https://opendata.dbd.go.th/api/action/datastore_search';
    const resourceId = 'f008dbbf-ddfa-4e3a-bac4-358a1a2b9853';
    const apiUrl = `${DBD_API_BASE}?resource_id=${resourceId}&filters=${encodeURIComponent(JSON.stringify({
      juristicID: formData.taxId
    }))}&limit=1`;
    try {
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          Accept: 'application/json'
        }
      });
      if (!response.ok) {
        throw new Error(`API returned status ${response.status}`);
      }
      const data = await response.json();
      if (!data.success || !data.result?.records?.length) {
        setIsLookingUp(false);
        setLookupError(`No company found for Tax ID ${formData.taxId}. Please verify the number and try again.`);
        return;
      }
      const record = data.result.records[0];
      const companyNameLocal = record.juristicNameTH || record.juristicNameTh || record.companyNameTH || '';
      const companyNameEn = record.juristicNameEN || record.juristicNameEn || record.companyNameEN || '';
      const addressFull = record.addressDesc || record.address || record.juristicAddress || '';
      const registerDate = record.registerDate || record.registrationDate || '';
      const juristicStatus = record.juristicStatus || record.status || '';
      const juristicType = record.juristicType || record.entityType || '';
      let addressLine1 = addressFull;
      let addressLine2 = '';
      let city = '';
      let postcode = '';
      const postcodeMatch = addressFull.match(/\b(\d{5})\b\s*$/);
      if (postcodeMatch) {
        postcode = postcodeMatch[1];
        addressLine1 = addressFull.replace(postcodeMatch[0], '').trim();
      }
      const parts = addressLine1.split(/[,;]/).map((s: string) => s.trim()).filter(Boolean);
      if (parts.length >= 3) {
        addressLine1 = parts.slice(0, Math.ceil(parts.length / 2)).join(', ');
        const remaining = parts.slice(Math.ceil(parts.length / 2));
        city = remaining.pop() || '';
        addressLine2 = remaining.join(', ');
      } else if (parts.length === 2) {
        addressLine1 = parts[0];
        city = parts[1];
      }
      setFormData(prev => ({
        ...prev,
        companyNameTh: companyNameLocal || prev.companyNameTh,
        companyNameEn: companyNameEn || prev.companyNameEn,
        addressLine1: addressLine1 || prev.addressLine1,
        addressLine2: addressLine2 || prev.addressLine2,
        city: city || prev.city,
        country: profileCountry(),
        postcode: postcode || prev.postcode
      }));
      setDbdMeta({
        registerDate,
        juristicStatus,
        juristicType,
        isMockFallback: false
      });
      setIsLookingUp(false);
      setLookupSuccess(true);
    } catch (err: any) {
      console.warn('DBD API call failed, using mock fallback:', err.message);
      setFormData(prev => ({
        ...prev,
        companyNameTh: makeCompanyName('Test Company'),
        companyNameEn: makeCompanyName('Test Company'),
        addressLine1: profileStreet(0),
        addressLine2: '',
        city: profileCity(0),
        country: profileCountry(),
        postcode: '50470'
      }));
      setDbdMeta({
        registerDate: '2020-03-15',
        juristicStatus: 'Active',
        juristicType: 'Limited Company',
        isMockFallback: true
      });
      setIsLookingUp(false);
      setLookupSuccess(true);
    }
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    navigate('/suppliers');
  };
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const {
      name,
      value,
      type
    } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  return <div className="max-w-3xl mx-auto space-y-6 pb-12" data-id="element-13931" data-mp="1cfdgo9uzksr5">
      {/* Header */}
      <div className="flex items-center space-x-4" data-id="element-13932" data-mp="1q8cxqgaf2v0t">
        <button onClick={() => navigate('/suppliers')} className="flex items-center text-gray-600 hover:text-gray-900 transition-colors text-sm font-medium" data-id="element-13933" data-mp="1l99oskrbg4nk">
          <ArrowLeftIcon size={16} className="mr-2" data-id="element-13934" data-mp="tilspn4i1pgr" />
          {t('pages.BulkSupplierUpload.back_to_suppliers')}
        </button>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-13935" data-mp="17j69kdqutb7m">
        <div className="px-8 py-6 border-b border-gray-100" data-id="element-13936" data-mp="12i8q7xu3yb4j">
          <h1 className="text-xl font-semibold text-gray-900" data-id="element-13937" data-mp="2ixa17d9yyn9i">
            {isEditing ? 'Edit Supplier' : 'Add New Supplier'}
          </h1>
        </div>

        <form onSubmit={handleSubmit} className="p-8 space-y-10" data-id="element-13938" data-mp="2dxxd6p7d3a5c">
          {/* SECTION 1: Supplier Information */}
          <div className="space-y-6" data-id="element-13939" data-mp="3vy6kfcfa7bve">
            <h2 className="text-lg font-medium text-gray-900" data-id="element-13940" data-mp="4qail8xd5vxr">
              {t('pages.SupplierForm.supplier_information')}
            </h2>

            {/* Entry Method Selection */}
            {!isEditing && <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-13941" data-mp="3rvi82urczmto">
                <label className={`relative flex cursor-pointer rounded-xl border p-4 hover:bg-gray-50 transition-colors ${entryMethod === 'manual' ? 'border-[#E2231A] ring-1 ring-[#E2231A] bg-red-50/30' : 'border-gray-200'}`} data-id="element-13942" data-mp="jex7o2csm226">
                  <input type="radio" name="entryMethod" value="manual" checked={entryMethod === 'manual'} onChange={() => setEntryMethod('manual')} className="sr-only" data-id="element-13943" data-mp="9oltzo3b4ei3" />

                  <div className="flex w-full items-start" data-id="element-13944" data-mp="176cadw8heldy">
                    <div className="flex items-center h-5" data-id="element-13945" data-mp="21uv4t714qg2c">
                      <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${entryMethod === 'manual' ? 'border-[#E2231A]' : 'border-gray-300'}`} data-id="element-13946" data-mp="26sejg81qouut">
                        {entryMethod === 'manual' && <div className="w-2 h-2 rounded-full bg-[#E2231A]" data-id="element-13947" data-mp="1wwbi34epp08x" />}
                      </div>
                    </div>
                    <div className="ml-3 flex-1" data-id="element-13948" data-mp="1nuu1lkxe6wjp">
                      <div className="flex items-center gap-2 mb-1" data-id="element-13949" data-mp="194wb2ybzpurc">
                        <PenLineIcon size={16} className={entryMethod === 'manual' ? 'text-[#9F140F]' : 'text-gray-500'} data-id="element-13950" data-mp="196pjai294n1g" />

                        <span className={`text-sm font-medium ${entryMethod === 'manual' ? 'text-gray-900' : 'text-gray-700'}`} data-id="element-13951" data-mp="155dtocjdo1t9">
                          {t('pages.SupplierForm.enter_details_myself')}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500" data-id="element-13952" data-mp="qglsuuiiw92u">
                        {t('pages.SupplierForm.fill_in_supplier_identity_contact_and')}
                      </p>
                    </div>
                  </div>
                </label>

                <label className={`relative flex cursor-pointer rounded-xl border p-4 hover:bg-gray-50 transition-colors ${entryMethod === 'invite' ? 'border-[#E2231A] ring-1 ring-[#E2231A] bg-red-50/30' : 'border-gray-200'}`} data-id="element-13953" data-mp="1e66pgg7ln4ul">
                  <input type="radio" name="entryMethod" value="invite" checked={entryMethod === 'invite'} onChange={() => setEntryMethod('invite')} className="sr-only" data-id="element-13954" data-mp="1n7e8xs5gi14m" />

                  <div className="flex w-full items-start" data-id="element-13955" data-mp="ktos1ibsyfqe">
                    <div className="flex items-center h-5" data-id="element-13956" data-mp="ousbeet26twd">
                      <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${entryMethod === 'invite' ? 'border-[#E2231A]' : 'border-gray-300'}`} data-id="element-13957" data-mp="tlqqjb2b9ly1">
                        {entryMethod === 'invite' && <div className="w-2 h-2 rounded-full bg-[#E2231A]" data-id="element-13958" data-mp="13i3lsddi87uw" />}
                      </div>
                    </div>
                    <div className="ml-3 flex-1" data-id="element-13959" data-mp="1ckwdghqlmll9">
                      <div className="flex items-center gap-2 mb-1" data-id="element-13960" data-mp="1ciifo14java0">
                        <LinkIcon size={16} className={entryMethod === 'invite' ? 'text-[#9F140F]' : 'text-gray-500'} data-id="element-13961" data-mp="3abpfdmlbavn9" />

                        <span className={`text-sm font-medium ${entryMethod === 'invite' ? 'text-gray-900' : 'text-gray-700'}`} data-id="element-13962" data-mp="320fd86hlg7m3">
                          {t('pages.SupplierForm.invite_supplier')}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500" data-id="element-13963" data-mp="3pqjei77b7v9x">
                        {t('pages.SupplierForm.generate_a_secure_link_for_the')}
                      </p>
                    </div>
                  </div>
                </label>
              </div>}

            {/* Supplier Type Toggle (Always visible) */}
            <div className="flex p-1 bg-gray-100 rounded-lg w-fit" data-id="element-13964" data-mp="25bheyeamxrk6">
              <button type="button" onClick={() => setSupplierType('company')} className={`flex items-center px-4 py-1.5 text-sm font-medium rounded-md transition-all ${supplierType === 'company' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-13965" data-mp="25csj4f7zt1bt">
                <BuildingIcon size={16} className="mr-2" data-id="element-13966" data-mp="3f8nxvzywfxza" />
                {t('pages.SupplierDetail.company')}
              </button>
              <button type="button" onClick={() => setSupplierType('individual')} className={`flex items-center px-4 py-1.5 text-sm font-medium rounded-md transition-all ${supplierType === 'individual' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-13967" data-mp="3jassb0x68ymz">
                <UserIcon size={16} className="mr-2" data-id="element-13968" data-mp="1vugrpqhmbyi" />
                {t('pages.SupplierDetail.individual')}
              </button>
            </div>

            {/* MANUAL ENTRY FLOW */}
            {entryMethod === 'manual' && <div className="space-y-8 animate-in fade-in slide-in-from-top-4 duration-300" data-id="element-13969" data-mp="2rj3gvmmb3asn">
                {/* --- COMPANY FIELDS --- */}
                {supplierType === 'company' && <div className="space-y-6" data-id="element-13970" data-mp="2f0r227twr3v3">
                    {/* DBD Lookup Section */}
                    <div className="bg-gray-50 p-5 rounded-xl border border-gray-200" data-id="element-13971" data-mp="2t0in2siudkil">
                      <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-13972" data-mp="2o15nr8asakz4">
                        {t('pages.SupplierForm.tax_id_registration_number')}
                      </label>
                      <div className="flex gap-3" data-id="element-13973" data-mp="1vc8f7wwdk2jk">
                        <input type="text" name="taxId" required maxLength={13} value={formData.taxId} onChange={handleChange} placeholder={t('pages.SupplierForm.attrs.13digit_registration_number')} className="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors bg-white" data-id="element-13974" data-mp="1vdtdnxlt85mp" />

                        <button type="button" onClick={handleLookup} disabled={isLookingUp} className="flex items-center px-5 py-2.5 bg-[#E2231A] hover:bg-[#EB4A42] disabled:bg-[#E2231A]/70 text-white text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-13975" data-mp="3me1sctx2nw6m">
                          {isLookingUp ? <Loader2Icon size={18} className="animate-spin" data-id="element-13976" data-mp="32p13t6u3xofs" /> : <>
                              <SearchIcon size={18} className="mr-2" data-id="element-13977" data-mp="37mj5cc5sp1dr" />
                              {t('pages.SupplierForm.lookup')}
                            </>}
                        </button>
                      </div>

                      {lookupError && <p className="mt-2 text-sm text-red-600" data-id="element-13978" data-mp="x4pok47jklui">
                          {lookupError}
                        </p>}

                      {lookupSuccess && <div className="mt-4 space-y-2" data-id="element-13979" data-mp="nafgle649eyo">
                          <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-start" data-id="element-13980" data-mp="jaf3pxyo1btq">
                            <CheckCircle2Icon size={20} className="text-green-600 mt-0.5 mr-3 flex-shrink-0" data-id="element-13981" data-mp="edon99iy8e7s" />

                            <div data-id="element-13982" data-mp="4efwepjdrfpn">
                              <p className="text-sm font-medium text-green-800" data-id="element-13983" data-mp="cc9n4r9gop2">
                                {t('pages.SupplierForm.company_details_fetched_from_department_of')}
                              </p>
                              <div className="mt-1 flex items-center flex-wrap gap-x-3 gap-y-1 text-xs text-green-700" data-id="element-13984" data-mp="1uo983ho4eutm">
                                {dbdMeta.registerDate && <>
                                    <span data-id="element-13985" data-mp="249k29e1ki59h">
                                      {t('pages.SupplierForm.registered')}{' '}
                                      {dbdMeta.registerDate}
                                    </span>
                                    <span className="w-1 h-1 rounded-full bg-green-400" data-id="element-13986" data-mp="1uaurs6c03n39"></span>
                                  </>}
                                {dbdMeta.juristicStatus && <span className="font-semibold" data-id="element-13987" data-mp="1lc0qw3rn0ajw">
                                    {dbdMeta.juristicStatus}
                                  </span>}
                                {dbdMeta.juristicType && <>
                                    <span className="w-1 h-1 rounded-full bg-green-400" data-id="element-13988" data-mp="3kn1wz2vrw6co"></span>
                                    <span data-id="element-13989" data-mp="3us0c71r7ohsm">{dbdMeta.juristicType}</span>
                                  </>}
                              </div>
                            </div>
                          </div>
                          {dbdMeta.isMockFallback && <div className="p-2.5 bg-amber-50 border border-amber-200 rounded-lg" data-id="element-13990" data-mp="2m4yf20dgo66">
                              <p className="text-xs text-amber-700" data-id="element-13991" data-mp="hb11slrhhd0g">
                                <span className="font-medium" data-id="element-13992" data-mp="ceami0ni6lg3">
                                  {t('pages.SupplierForm.note')}
                                </span>{' '}
                                {t('pages.SupplierForm.direct_browser_request_to_dbd_api')}
                              </p>
                            </div>}
                        </div>}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-13993" data-mp="1vgbpxqaotmuf">
                      <div data-id="element-13994" data-mp="1qia1epuhsvbk">
                        <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-13995" data-mp="1qgz3h6nqahsd">
                          {t('pages.SupplierForm.company_name_local')}
                        </label>
                        <input type="text" name="companyNameTh" required value={formData.companyNameTh} onChange={handleChange} className={`w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors ${lookupSuccess ? 'bg-yellow-50' : ''}`} data-id="element-13996" data-mp="208uyu9yfjtsw" />
                      </div>
                      <div data-id="element-13997" data-mp="2ww9ew6069oqe">
                        <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-13998" data-mp="2swhfaisrasx6">
                          {t('pages.SupplierDetail.company_name_english')}
                        </label>
                        <input type="text" name="companyNameEn" value={formData.companyNameEn} onChange={handleChange} className={`w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors ${lookupSuccess ? 'bg-yellow-50' : ''}`} data-id="element-13999" data-mp="35f39kr8kpom6" />
                      </div>
                      <div data-id="element-14000" data-mp="3o7f9767uebsm">
                        <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-14001" data-mp="39h821mnacf78">
                          {t('pages.SupplierForm.branch_number')}
                        </label>
                        <input type="text" name="branch" required value={formData.branch} onChange={handleChange} placeholder="00000" className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-14002" data-mp="607ymz4bwspt" />
                      </div>
                      <div data-id="element-14003" data-mp="6nothsjnf5km">
                        <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-14004" data-mp="6mdpbrmajvsz">
                          {t('pages.SupplierForm.contact_person_name')}
                        </label>
                        <input type="text" name="contactPerson" value={formData.contactPerson} onChange={handleChange} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-14005" data-mp="3t0x6fw2nhp35" />
                      </div>
                    </div>

                    <label className="flex items-center cursor-pointer group w-fit" data-id="element-14006" data-mp="1pkiexb93e40p">
                      <div className="relative flex items-center justify-center" data-id="element-14007" data-mp="1ujvbwotogrh0">
                        <input type="checkbox" name="vatRegistered" checked={formData.vatRegistered} onChange={handleChange} className="peer sr-only" data-id="element-14008" data-mp="1zk0xbn9cthqn" />

                        <div className="w-5 h-5 border-2 border-gray-300 rounded bg-white peer-checked:bg-[#E2231A] peer-checked:border-[#E2231A] transition-colors" data-id="element-14009" data-mp="1btncoht3ml63"></div>
                        <svg className="absolute w-3.5 h-3.5 text-white pointer-events-none opacity-0 peer-checked:opacity-100 transition-opacity" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" data-id="element-14010" data-mp="1ftxgpoxbz1ib">
                          <polyline points="20 6 9 17 4 12" data-id="element-14011" data-mp="4417kv7d6kdg"></polyline>
                        </svg>
                      </div>
                      <span className="ml-3 text-sm font-medium text-gray-900 group-hover:text-gray-700" data-id="element-14012" data-mp="it758eqk02j8">
                        {t('pages.SupplierForm.vat_registered_company')}
                      </span>
                    </label>
                  </div>}

                {/* --- INDIVIDUAL FIELDS --- */}
                {supplierType === 'individual' && <div className="space-y-6" data-id="element-14013" data-mp="11pgp5io4znb5">
                    <div className="flex gap-6 mb-2" data-id="element-14014" data-mp="wqlyjujxgugh">
                      <label className="flex items-center cursor-pointer" data-id="element-14015" data-mp="wrx3hxp40896">
                        <input type="radio" name="idType" value="citizen_id" checked={formData.idType === 'citizen_id'} onChange={handleChange} className="w-4 h-4 text-[#E2231A] border-gray-300 focus:ring-[#E2231A]" data-id="element-14016" data-mp="33iecx0323pp8" />

                        <span className="ml-2 text-sm font-medium text-gray-700" data-id="element-14017" data-mp="149v7xnsfot5j">
                          {t('pages.SupplierForm.thai_citizen_id')}
                        </span>
                      </label>
                      <label className="flex items-center cursor-pointer" data-id="element-14018" data-mp="v8eybi9g0v2d">
                        <input type="radio" name="idType" value="passport" checked={formData.idType === 'passport'} onChange={handleChange} className="w-4 h-4 text-[#E2231A] border-gray-300 focus:ring-[#E2231A]" data-id="element-14019" data-mp="zcmlhb0962yh" />

                        <span className="ml-2 text-sm font-medium text-gray-700" data-id="element-14020" data-mp="2knv4cos2m0o1">
                          {t('pages.SupplierForm.passport')}
                        </span>
                      </label>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-id="element-14021" data-mp="2e7cspxbn7ipl">
                      <div className="md:col-span-2" data-id="element-14022" data-mp="29amgxm6vf99z">
                        <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-14023" data-mp="24ahwxm6r7wf0">
                          {formData.idType === 'citizen_id' ? 'Citizen ID Number *' : 'Passport Number *'}
                        </label>
                        <input type="text" name="idNumber" required maxLength={formData.idType === 'citizen_id' ? 13 : undefined} value={formData.idNumber} onChange={handleChange} placeholder={formData.idType === 'citizen_id' ? '13-digit ID number' : 'Passport number'} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-14024" data-mp="2x1i0xx53506o" />
                      </div>
                      <div data-id="element-14025" data-mp="az2rw2wmzfui">
                        <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-14026" data-mp="azvq2fhj4x11">
                          {t('pages.SupplierForm.full_name_local')}
                        </label>
                        <input type="text" name="fullNameTh" value={formData.fullNameTh} onChange={handleChange} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-14027" data-mp="jsz6mwnh1pzw" />
                      </div>
                      <div data-id="element-14028" data-mp="1f1u07jj2r9d7">
                        <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-14029" data-mp="1f0ip0ynauzc2">
                          {t('pages.SupplierForm.full_name')}
                        </label>
                        <input type="text" name="fullNameEn" required value={formData.fullNameEn} onChange={handleChange} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-14030" data-mp="1o20bcyravx97" />
                      </div>
                      <div className="md:col-span-2" data-id="element-14031" data-mp="hlwq2pklpkot">
                        <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-14032" data-mp="hkd7gsbh2xjv">
                          {t('pages.SupplierForm.tax_id_if_different_from_citizen')}
                        </label>
                        <input type="text" name="individualTaxId" value={formData.individualTaxId} onChange={handleChange} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-14033" data-mp="2t1ezkqk91jmr" />
                      </div>
                    </div>
                  </div>}

                {/* --- SHARED CONTACT FIELDS --- */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5 pt-4 border-t border-gray-100" data-id="element-14034" data-mp="32z2yztssvfb9">
                  <div data-id="element-14035" data-mp="1mlcu4ktuwn0k">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-14036" data-mp="1mk1mub17cx4p">
                      {t('pages.SupplierForm.email_address')}
                    </label>
                    <input type="email" name="email" required value={formData.email} onChange={handleChange} placeholder={t('pages.SupplierForm.attrs.enter_email_address')} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-14037" data-mp="201jka446bhgz" />
                  </div>
                  <div data-id="element-14038" data-mp="1m7aehz3s5jdc">
                    <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-14039" data-mp="1r86kci7ozfle">
                      {t('pages.SupplierForm.phone_number')}
                    </label>
                    <input type="tel" name="phone" value={formData.phone} onChange={handleChange} placeholder={t('pages.SupplierForm.attrs.eg_0812345678')} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-14040" data-mp="1w19d4o4nal0j" />
                  </div>
                  <div className="md:col-span-2" data-id="element-14041" data-mp="37utkuvewyeqz">
                    <label className="flex items-center cursor-pointer group w-fit" data-id="element-14042" data-mp="32ufbna1ksyrd">
                      <div className="relative flex items-center justify-center" data-id="element-14043" data-mp="2xv2r4vy6g3ry">
                        <input type="checkbox" name="useAsBillingEmail" checked={formData.useAsBillingEmail} onChange={handleChange} className="peer sr-only" data-id="element-14044" data-mp="2t3ui410rekfb" />

                        <div className="w-5 h-5 border-2 border-gray-300 rounded bg-white peer-checked:bg-[#E2231A] peer-checked:border-[#E2231A] transition-colors" data-id="element-14045" data-mp="2f4l14z4qbmaa"></div>
                        <svg className="absolute w-3.5 h-3.5 text-white pointer-events-none opacity-0 peer-checked:opacity-100 transition-opacity" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" data-id="element-14046" data-mp="2a7b3wxb3m6bw">
                          <polyline points="20 6 9 17 4 12" data-id="element-14047" data-mp="2j8dssxfnvwn5"></polyline>
                        </svg>
                      </div>
                      <span className="ml-3 text-sm font-medium text-gray-900 group-hover:text-gray-700" data-id="element-14048" data-mp="2o8rpjjbtbk5v">
                        {t('pages.SupplierForm.use_this_email_as_the_billing')}
                      </span>
                    </label>
                  </div>
                </div>

                {/* Divider: Billing information */}
                <div className="relative py-2" data-id="element-14049" data-mp="36xojfm3asank">
                  <div className="absolute inset-0 flex items-center" data-id="element-14050" data-mp="36zj91dpikqxz">
                    <div className="w-full border-t border-gray-200" data-id="element-14051" data-mp="vh4tt27s0z11"></div>
                  </div>
                  <div className="relative flex justify-center" data-id="element-14052" data-mp="qil5ww9mhd61">
                    <span className="bg-white px-4 text-sm font-medium text-gray-500 uppercase tracking-wider" data-id="element-14053" data-mp="158sfsnlkdnv4">
                      {t('pages.SupplierForm.billing_information')}
                    </span>
                  </div>
                </div>

                {/* Billing Info Fields */}
                <div className="space-y-5" data-id="element-14054" data-mp="7l0i7silgkn0">
                  <input type="text" name="addressLine1" value={formData.addressLine1} onChange={handleChange} placeholder={t('pages.SupplierForm.attrs.address_line_1')} className={`w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors ${lookupSuccess && supplierType === 'company' ? 'bg-yellow-50' : ''}`} data-id="element-14055" data-mp="7m1xyaak1vb2" />

                  <input type="text" name="addressLine2" value={formData.addressLine2} onChange={handleChange} placeholder={t('pages.SupplierForm.attrs.address_line_2')} className={`w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors ${lookupSuccess && supplierType === 'company' ? 'bg-yellow-50' : ''}`} data-id="element-14056" data-mp="7ft57peo98o1" />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-14057" data-mp="v55bohim72mn">
                    <div data-id="element-14058" data-mp="z6qwcp1e4l7e">
                      <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-14059" data-mp="z5xkxarhl7ix">
                        {t('pages.SupplierForm.city_district')}
                      </label>
                      <input type="text" name="city" value={formData.city} onChange={handleChange} placeholder={t('pages.SupplierForm.attrs.eg_khlong_toei')} className={`w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors ${lookupSuccess && supplierType === 'company' ? 'bg-yellow-50' : ''}`} data-id="element-14060" data-mp="194n3z6v4cy0r" />
                    </div>
                    <div data-id="element-14061" data-mp="1x1ji6nqk1oz5">
                      <label className="block text-xs font-medium text-gray-500 mb-1" data-id="element-14062" data-mp="1x19wvdggg5zs">
                        {t('pages.BulkSupplierUpload.province')}
                      </label>
                      <SearchableSelect name="province" options={MALAYSIAN_STATES} value={formData.province} onChange={val => setFormData(prev => ({
                    ...prev,
                    province: val
                  }))} placeholder={t('pages.SupplierForm.attrs.select_province')} data-id="element-14063" data-mp="1o7oku1vod2u2" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-14064" data-mp="990s4na8ijfw">
                    <div className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-700 flex items-center" data-id="element-14065" data-mp="3rfxc2akywlze">
                      {t('pages.SupplierForm.thailand')}
                    </div>
                    <input type="text" name="postcode" value={formData.postcode} onChange={handleChange} placeholder={t('pages.SupplierForm.attrs.postcode_5_digits')} className={`w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors ${lookupSuccess && supplierType === 'company' ? 'bg-yellow-50' : ''}`} data-id="element-14066" data-mp="3hkn8vjiu28k1" />
                  </div>
                </div>

                {/* Divider: Additional information */}
                <div className="relative py-2" data-id="element-14067" data-mp="3a6358x8ixot0">
                  <div className="absolute inset-0 flex items-center" data-id="element-14068" data-mp="3ny9iaagdrfo8">
                    <div className="w-full border-t border-gray-200" data-id="element-14069" data-mp="3jx7jfuqdl5ms"></div>
                  </div>
                  <div className="relative flex justify-center" data-id="element-14070" data-mp="3jzk1e7w4vfzu">
                    <span className="bg-white px-4 text-sm font-medium text-gray-500 uppercase tracking-wider" data-id="element-14071" data-mp="1r2ogzeqi2kl">
                      {t('pages.SupplierForm.additional_information')}
                    </span>
                  </div>
                </div>

                {/* Additional Info Fields */}
                <div data-id="element-14072" data-mp="1ylfmjbc133oh">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-14073" data-mp="1ym7emc6f2ja0">
                    {t('pages.SupplierDetail.internal_reference')}
                  </label>
                  <input type="text" name="internalReference" value={formData.internalReference} onChange={handleChange} placeholder={t('pages.SupplierForm.attrs.enter_internal_reference')} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors" data-id="element-14074" data-mp="1hnyzcxi10gi6" />
                </div>

                {/* Divider: Payout method */}
                <div className="relative py-2" data-id="element-14075" data-mp="18qok7123877z">
                  <div className="absolute inset-0 flex items-center" data-id="element-14076" data-mp="2f5fqxldw6m9x">
                    <div className="w-full border-t border-gray-200" data-id="element-14077" data-mp="2f7ihc6iyihf6"></div>
                  </div>
                  <div className="relative flex justify-center" data-id="element-14078" data-mp="2k63j4adux95q">
                    <span className="bg-white px-4 text-sm font-medium text-gray-500 uppercase tracking-wider" data-id="element-14079" data-mp="8flznusdqd8l">
                      {t('pages.SupplierDetail.payout_method')}
                    </span>
                  </div>
                </div>

                <p className="text-xs text-gray-500 -mt-4" data-id="element-14080" data-mp="3pxev8we60ipy">
                  {t('pages.SupplierForm.choose_how_this_supplier_will_receive')}
                </p>

                {/* Payout Method Selection */}
                <div className="space-y-4" data-id="element-14081" data-mp="3gvxfjf4o6j1s">
                  {/* Bank Account Option */}
                  <div className={`border rounded-xl transition-colors ${formData.payoutMethod === 'bank_account' ? 'border-[#E2231A] ring-1 ring-[#E2231A]' : 'border-gray-200'}`} data-id="element-14082" data-mp="3byf7rs2e29vd">
                    <label className="flex items-center p-4 cursor-pointer hover:bg-gray-50" data-id="element-14083" data-mp="1a5hl6dz7gdiv">
                      <input type="radio" name="payoutMethod" value="bank_account" checked={formData.payoutMethod === 'bank_account'} onChange={handleChange} className="w-4 h-4 text-[#E2231A] border-gray-300 focus:ring-[#E2231A] mt-0.5" data-id="element-14084" data-mp="157pluyw8dp4n" />

                      <div className="ml-3 flex items-center" data-id="element-14085" data-mp="1ea4r3p87x50p">
                        <div className="w-8 h-8 rounded-full bg-green-50 flex items-center justify-center mr-3" data-id="element-14086" data-mp="2axhs3k1h9o12">
                          <LandmarkIcon size={16} className="text-green-600" data-id="element-14087" data-mp="26y8yqgacns61" />
                        </div>
                        <div data-id="element-14088" data-mp="224obnjaaoilm">
                          <div className="text-sm font-medium text-gray-900" data-id="element-14089" data-mp="223wew54yoetr">
                            {t('pages.SupplierDetail.bank_account')}
                          </div>
                          <div className="text-xs text-gray-500" data-id="element-14090" data-mp="2unuyp5yjqbf3">
                            {t('pages.SupplierForm.direct_transfer_to_a_thai_bank')}
                          </div>
                        </div>
                      </div>
                    </label>

                    {formData.payoutMethod === 'bank_account' && <div className="p-4 bg-gray-50 border-t border-gray-100 space-y-4 rounded-b-xl" data-id="element-14091" data-mp="373undkb191oi">
                        <div data-id="element-14092" data-mp="3b4mw8cfzt4k9">
                          <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-14093" data-mp="3g63v62ex2gy7">
                            {t('pages.SupplierDetail.bank_name')}
                          </label>
                          <SearchableSelect name="bankName" options={MALAYSIAN_BANKS} value={formData.bankName} onChange={val => setFormData(prev => ({
                      ...prev,
                      bankName: val
                    }))} placeholder={t('pages.SupplierForm.attrs.select_a_bank')} data-id="element-14094" data-mp="3pvne182mqat2" />
                        </div>
                        <div data-id="element-14095" data-mp="2djqizf9cx5a7">
                          <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-14096" data-mp="2dktbtsq8phsr">
                            {t('pages.SupplierDetail.account_number')}
                          </label>
                          <input type="text" name="accountNumber" maxLength={12} placeholder={t('pages.SupplierForm.attrs.1012_digits')} value={formData.accountNumber} onChange={handleChange} className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:ring-1 focus:ring-[#E2231A] focus:border-[#E2231A]" data-id="element-14097" data-mp="2nj90lp1soez3" />
                        </div>
                        <div data-id="element-14098" data-mp="3droo0s05gh3m">
                          <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-14099" data-mp="3dpu33dovoozj">
                            {t('pages.SupplierDetail.account_name')}
                          </label>
                          <input type="text" name="accountName" placeholder={t('pages.SupplierForm.attrs.name_on_the_bank_account')} value={formData.accountName} onChange={handleChange} className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:ring-1 focus:ring-[#E2231A] focus:border-[#E2231A]" data-id="element-14100" data-mp="34012f7q9xua2" />
                        </div>
                      </div>}
                  </div>

                  {/* PromptPay Option */}
                  <div className={`border rounded-xl transition-colors ${formData.payoutMethod === 'promptpay' ? 'border-[#E2231A] ring-1 ring-[#E2231A]' : 'border-gray-200'}`} data-id="element-14101" data-mp="3qpcrhh5vqfcl">
                    <label className="flex items-center p-4 cursor-pointer hover:bg-gray-50" data-id="element-14102" data-mp="3gtrrtpoa9r60">
                      <input type="radio" name="payoutMethod" value="promptpay" checked={formData.payoutMethod === 'promptpay'} onChange={handleChange} className="w-4 h-4 text-[#E2231A] border-gray-300 focus:ring-[#E2231A] mt-0.5" data-id="element-14103" data-mp="3buonk7g4noz3" />

                      <div className="ml-3 flex items-center" data-id="element-14104" data-mp="2szzw8nlatd2e">
                        <div className="w-8 h-8 rounded-full bg-purple-50 flex items-center justify-center mr-3" data-id="element-14105" data-mp="2o1ydk3rsbfrg">
                          <SmartphoneIcon size={16} className="text-purple-600" data-id="element-14106" data-mp="2k1y12oo8xen1" />
                        </div>
                        <div data-id="element-14107" data-mp="13jnsu6qn5hnn">
                          <div className="text-sm font-medium text-gray-900" data-id="element-14108" data-mp="13n1utdve7ouk">
                            {t('pages.SupplierDetail.promptpay')}
                          </div>
                          <div className="text-xs text-gray-500" data-id="element-14109" data-mp="1dimw19sn0l39">
                            {t('pages.SupplierForm.fast_transfer_via_phone_citizen_id')}
                          </div>
                        </div>
                      </div>
                    </label>

                    {formData.payoutMethod === 'promptpay' && <div className="p-4 bg-gray-50 border-t border-gray-100 space-y-4 rounded-b-xl" data-id="element-14110" data-mp="ulvccz96o3yo">
                        <div data-id="element-14111" data-mp="3ta0xj23u7e8z">
                          <label className="block text-xs font-medium text-gray-700 mb-2" data-id="element-14112" data-mp="23zvfj1unuzh">
                            {t('pages.SupplierForm.promptpay_type')}
                          </label>
                          <div className="flex flex-wrap gap-4" data-id="element-14113" data-mp="3q6bmjcbo058f">
                            <label className="flex items-center" data-id="element-14114" data-mp="3m6ky3f9m2172">
                              <input type="radio" name="promptPayType" value="phone" checked={formData.promptPayType === 'phone'} onChange={handleChange} className="w-4 h-4 text-[#E2231A] border-gray-300 focus:ring-[#E2231A]" data-id="element-14115" data-mp="3td1j1x9q2y8" />

                              <span className="ml-2 text-sm text-gray-700" data-id="element-14116" data-mp="3h5wzo8j1yi2t">
                                {t('pages.SupplierForm.phone_number_2')}
                              </span>
                            </label>
                            <label className="flex items-center" data-id="element-14117" data-mp="rio6kvlniji6">
                              <input type="radio" name="promptPayType" value="citizen_id" checked={formData.promptPayType === 'citizen_id'} onChange={handleChange} className="w-4 h-4 text-[#E2231A] border-gray-300 focus:ring-[#E2231A]" data-id="element-14118" data-mp="rifv9856fm93" />

                              <span className="ml-2 text-sm text-gray-700" data-id="element-14119" data-mp="2x5vmhubg8ovl">
                                {t('pages.SupplierForm.citizen_id')}
                              </span>
                            </label>
                            <label className="flex items-center" data-id="element-14120" data-mp="2od1ssrv99my7">
                              <input type="radio" name="promptPayType" value="tax_id" checked={formData.promptPayType === 'tax_id'} onChange={handleChange} className="w-4 h-4 text-[#E2231A] border-gray-300 focus:ring-[#E2231A]" data-id="element-14121" data-mp="2ob8kz98wowom" />

                              <span className="ml-2 text-sm text-gray-700" data-id="element-14122" data-mp="172v26nccjet7">
                                {t('pages.BulkSupplierUpload.tax_id')}
                              </span>
                            </label>
                          </div>
                        </div>
                        <div data-id="element-14123" data-mp="1uqw8dpm1yj5g">
                          <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-14124" data-mp="1uspen3sr71cx">
                            {t('pages.SupplierDetail.promptpay_id')}
                          </label>
                          <input type="text" name="promptPayId" placeholder={formData.promptPayType === 'phone' ? '0XX-XXX-XXXX' : 'X-XXXX-XXXXX-XX-X'} value={formData.promptPayId} onChange={handleChange} className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:ring-1 focus:ring-[#E2231A] focus:border-[#E2231A]" data-id="element-14125" data-mp="1lpy02v4kzph2" />
                        </div>
                        <div data-id="element-14126" data-mp="34q6zfdbypqwz">
                          <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-14127" data-mp="34ovv9celuh5c">
                            {t('pages.SupplierForm.display_name_for_verification')}
                          </label>
                          <input type="text" name="promptPayName" value={formData.promptPayName} onChange={handleChange} className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:ring-1 focus:ring-[#E2231A] focus:border-[#E2231A]" data-id="element-14128" data-mp="3ipf4gmut3t93" />
                        </div>
                      </div>}
                  </div>
                </div>
              </div>}

            {/* INVITE SUPPLIER FLOW */}
            {entryMethod === 'invite' && <div className="space-y-6 animate-in fade-in slide-in-from-top-4 duration-300" data-id="element-14129" data-mp="14vi37q1kqd15">
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-6" data-id="element-14130" data-mp="1slm77yioceso">
                  <h3 className="text-sm font-medium text-gray-900 mb-2" data-id="element-14131" data-mp="1xlqnpojl19iw">
                    {t('pages.SupplierForm.invite_supplier_2')}
                  </h3>
                  <p className="text-sm text-gray-600 mb-6" data-id="element-14132" data-mp="1nnkm781ou5ek">
                    {t('pages.SupplierForm.enter_a_name_for_this_supplier')}
                  </p>

                  <div className="space-y-4" data-id="element-14133" data-mp="c975cn5cqq81">
                    <div data-id="element-14134" data-mp="ca8kb2phnwv1">
                      <label className="block text-xs font-medium text-gray-700 mb-1.5" data-id="element-14135" data-mp="cai9io2plzzo">
                        {t('pages.SupplierForm.supplier_name')}
                      </label>
                      <input type="text" value={inviteName} onChange={e => setInviteName(e.target.value)} placeholder={t('pages.SupplierForm.attrs.eg_siam_fresh_market')} className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#E2231A]/20 focus:border-[#E2231A] transition-colors bg-white" data-id="element-14136" data-mp="3vvfrttijpaua" />
                    </div>

                    {!linkGenerated && <button type="button" onClick={() => {
                  if (inviteName.trim()) setLinkGenerated(true);
                }} disabled={!inviteName.trim()} className="flex items-center px-5 py-2.5 bg-[#E2231A] hover:bg-[#EB4A42] disabled:bg-gray-300 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-14137" data-mp="3n22qq4abv2x4">
                        <LinkIcon size={16} className="mr-2" data-id="element-14138" data-mp="3svz3rirghml7" />
                        {t('pages.SupplierForm.generate_invite_link')}
                      </button>}

                    {linkGenerated && <div className="space-y-4 animate-in fade-in slide-in-from-top-2 duration-200" data-id="element-14139" data-mp="316maahmcm93x">
                        <div data-id="element-14140" data-mp="357o76rssm92q">
                          <label className="block text-xs font-medium text-gray-700 mb-1.5" data-id="element-14141" data-mp="3a4ogrcouptyl">
                            {t('pages.SupplierForm.secure_link')}
                          </label>
                          <div className="flex gap-2" data-id="element-14142" data-mp="pf8jpzz1h90i">
                            <input type="text" readOnly value={inviteLink} className="flex-1 px-4 py-2.5 border border-gray-200 rounded-lg text-sm bg-white text-gray-600 font-mono" data-id="element-14143" data-mp="pgtdhndj4nxb" />

                            <button type="button" onClick={handleCopyLink} className="flex items-center justify-center px-4 py-2.5 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 text-sm font-medium rounded-lg transition-colors shadow-sm min-w-[100px]" data-id="element-14144" data-mp="255rlo8n9q5oq">
                              {copied ? <>
                                  <CheckIcon size={16} className="mr-2 text-green-600" data-id="element-14145" data-mp="1m99kz1qfyvgc" />{' '}
                                  {t('pages.SupplierForm.copied')}
                                </> : <>
                                  <CopyIcon size={16} className="mr-2" data-id="element-14146" data-mp="3jncb14yitjfg" />{' '}
                                  {t('pages.SupplierForm.copy')}
                                </>}
                            </button>
                          </div>
                        </div>

                        <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg" data-id="element-14147" data-mp="3em53n70aim9b">
                          <div className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" data-id="element-14148" data-mp="30u8g7g9kh0j9"></div>
                          <span className="text-sm font-medium text-amber-800" data-id="element-14149" data-mp="35p4hz84lv29k">
                            {t('pages.SupplierForm.pending_waiting_for_supplier_to_complete')}
                          </span>
                        </div>
                      </div>}
                  </div>
                </div>
              </div>}
          </div>

          {/* SECTION 2: Funds Source (Pay-in) - ALWAYS VISIBLE */}
          <div className="space-y-6 pt-8 border-t border-gray-200" data-id="element-14150" data-mp="23cl9x0x2hmoq">
            <div data-id="element-14151" data-mp="1yd8brk0qwt6u">
              <h2 className="text-lg font-medium text-gray-900" data-id="element-14152" data-mp="1ydqgz65dyhqw">
                {t('pages.SupplierForm.funds_source_payin')}
              </h2>
              <p className="text-sm text-gray-500 mt-1" data-id="element-14153" data-mp="27bu241cglci6">
                {t('pages.SupplierForm.choose_how_you_will_fund_payments')}
              </p>
            </div>

            {/* Pay-in Method Selection */}
            <div className="space-y-4" data-id="element-14154" data-mp="2h9gkixsyvm6w">
              {/* Acquiring Volume Option */}
              <div className={`border rounded-xl transition-colors ${formData.payInMethod === 'acquiring' ? 'border-[#E2231A] ring-1 ring-[#E2231A]' : 'border-gray-200'}`} data-id="element-14155" data-mp="2v2g1z5cb7qh3">
                <label className="flex items-center p-4 cursor-pointer hover:bg-gray-50" data-id="element-14156" data-mp="34zaxrgh77cro">
                  <input type="radio" name="payInMethod" value="acquiring" checked={formData.payInMethod === 'acquiring'} onChange={handleChange} className="w-4 h-4 text-[#E2231A] border-gray-300 focus:ring-[#E2231A] mt-0.5" data-id="element-14157" data-mp="301j34ep65cjs" />

                  <div className="ml-3 flex items-center" data-id="element-14158" data-mp="26mggbra9fzyq">
                    <div className="w-8 h-8 rounded-full bg-green-50 flex items-center justify-center mr-3" data-id="element-14159" data-mp="2bfrobvqt6ms8">
                      <LandmarkIcon size={16} className="text-green-600" data-id="element-14160" data-mp="2gdktclgdlaqx" />
                    </div>
                    <div data-id="element-14161" data-mp="2lbmdl9pjfg3z">
                      <div className="text-sm font-medium text-gray-900" data-id="element-14162" data-mp="2lf0hwnhrhze2">
                        {t('pages.SupplierForm.acquiring_volume')}
                      </div>
                      <div className="text-xs text-gray-500" data-id="element-14163" data-mp="2udc50fjf6f1r">
                        {t('pages.SupplierForm.use_your_merchant_acquiring_balance_funds')}
                      </div>
                    </div>
                  </div>
                </label>

                {formData.payInMethod === 'acquiring' && <div className="p-4 bg-gray-50 border-t border-gray-100 rounded-b-xl" data-id="element-14164" data-mp="npbq8kv7bkgc">
                    <p className="text-xs text-gray-600" data-id="element-14165" data-mp="ipz7ab7gaxj1">
                      {t('pages.SupplierForm.supplier_payments_will_be_deducted_from')}
                    </p>
                  </div>}
              </div>

              {/* Card Payment Option */}
              <div className={`border rounded-xl transition-colors ${formData.payInMethod === 'card' ? 'border-[#E2231A] ring-1 ring-[#E2231A]' : 'border-gray-200'}`} data-id="element-14166" data-mp="16h4lklxqthr8">
                <label className="flex items-center p-4 cursor-pointer hover:bg-gray-50" data-id="element-14167" data-mp="xed8khp7ydxh">
                  <input type="radio" name="payInMethod" value="card" checked={formData.payInMethod === 'card'} onChange={handleChange} className="w-4 h-4 text-[#E2231A] border-gray-300 focus:ring-[#E2231A] mt-0.5" data-id="element-14168" data-mp="3m7ha248d9ecl" />

                  <div className="ml-3 flex items-center" data-id="element-14169" data-mp="1q1q5m9fljqcy">
                    <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center mr-3" data-id="element-14170" data-mp="1c5mmpca6b4fs">
                      <CreditCardIcon size={16} className="text-blue-600" data-id="element-14171" data-mp="1h3xwjn0gxey0" />
                    </div>
                    <div data-id="element-14172" data-mp="24tigo98cyi8v">
                      <div className="text-sm font-medium text-gray-900" data-id="element-14173" data-mp="24vcvd5t1de4i">
                        {t('pages.SupplierForm.card_payment')}
                      </div>
                      <div className="text-xs text-gray-500" data-id="element-14174" data-mp="1v2nvz9arfqpj">
                        {t('pages.SupplierForm.link_your_own_debit_or_credit')}
                      </div>
                    </div>
                  </div>
                </label>

                {formData.payInMethod === 'card' && <div className="p-4 bg-gray-50 border-t border-gray-100 space-y-4 rounded-b-xl" data-id="element-14175" data-mp="2sk4g5c4aqu79">
                    <p className="text-xs text-gray-500" data-id="element-14176" data-mp="31vtrrngb3qco">
                      {t('pages.SupplierForm.your_card_will_be_charged_each')}
                    </p>
                    <div data-id="element-14177" data-mp="3bnppuyie7ggw">
                      <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-14178" data-mp="3blv6homrrwex">
                        {t('pages.SupplierForm.name_on_card')}
                      </label>
                      <input type="text" name="cardholderName" placeholder={t('pages.SupplierForm.attrs.your_name_as_it_appears_on')} value={formData.cardholderName} onChange={handleChange} className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:ring-1 focus:ring-[#E2231A] focus:border-[#E2231A]" data-id="element-14179" data-mp="2ixqz8yojvba4" />
                    </div>
                    <div className="grid grid-cols-3 gap-4" data-id="element-14180" data-mp="39r04em0krbu">
                      <div className="col-span-2" data-id="element-14181" data-mp="13cmmhe7ldnav">
                        <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-14182" data-mp="yd9qa2utz3vm">
                          {t('pages.SupplierDetail.card_number')}
                        </label>
                        <input type="text" name="cardNumber" placeholder={t('pages.SupplierForm.attrs.xxxx_xxxx_xxxx_xxxx')} value={formData.cardNumber} onChange={handleChange} className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:ring-1 focus:ring-[#E2231A] focus:border-[#E2231A]" data-id="element-14183" data-mp="18b7bsilb2mkj" />
                      </div>
                      <div data-id="element-14184" data-mp="251q1hrja85g3">
                        <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-14185" data-mp="202dixizg1kjb">
                          {t('pages.SupplierForm.expiry_date')}
                        </label>
                        <input type="text" name="expiryDate" placeholder={t('pages.SupplierForm.attrs.mmyy')} value={formData.expiryDate} onChange={handleChange} className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:ring-1 focus:ring-[#E2231A] focus:border-[#E2231A]" data-id="element-14186" data-mp="2jmcc5co4avjc" />
                      </div>
                    </div>
                  </div>}
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-end space-x-3 pt-6 border-t border-gray-100" data-id="element-14187" data-mp="1vxt1iqhli6o3">
            <button type="button" onClick={() => navigate('/suppliers')} className="px-6 py-2.5 border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors" data-id="element-14188" data-mp="1hy9yud7jznj6">
              {t('pages.BulkSupplierUpload.cancel')}
            </button>
            <button type="submit" className="px-6 py-2.5 bg-[#E2231A] hover:bg-[#EB4A42] text-white text-sm font-medium rounded-lg transition-colors shadow-sm" data-id="element-14189" data-mp="3jvk5wnzvi36q">
              {t('pages.SupplierForm.save_supplier')}
            </button>
          </div>
        </form>
      </div>
    </div>;
}